/**
 * Ventanas de **Finanzas → Cuadres**: subir el Excel del período y ver la ficha de
 * un cuadre.
 *
 * Son las tres vistas del panel anterior (`nuevo.php`, `ver.php` y `anular.php`): el
 * formulario avisa **mientras se escribe** si ese mes ya tiene un cuadre activo —lo
 * que obliga a anularlo antes— y la ficha enseña el archivo, su tamaño, quién lo
 * subió, la comprobación de integridad y, si sigue activo, la anulación con motivo.
 */

import { Download, FileSpreadsheet, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { cn, formatDateValue } from '@/lib/utils';

import { cuadresApi, useExisteCuadre } from '../cuadres.api';
import type { Cuadre, DetalleCuadre } from '../cuadres.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const ACEPTADOS = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

/** Un dato de la ficha, con su etiqueta. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
        {etiqueta}
      </span>
      <span className="text-sm break-words text-ink-800">{valor}</span>
    </div>
  );
}

interface DialogoSubirProps {
  abierto: boolean;
  anios: number[];
  meses: { valor: number; nombre: string }[];
  hoy: string;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onSubir: (datos: {
    mes: string;
    anio: string;
    observaciones: string;
    archivo: File | null;
  }) => void;
}

/** Formulario de subida del cuadre. */
export function DialogoSubirCuadre({
  abierto,
  anios,
  meses,
  hoy,
  guardando,
  error,
  onCerrar,
  onSubir,
}: DialogoSubirProps) {
  const [mes, setMes] = useState(String(Number(hoy.slice(5, 7))));
  const [anio, setAnio] = useState(hoy.slice(0, 4));
  const [observaciones, setObservaciones] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);

  const existente = useExisteCuadre(Number(mes), Number(anio));
  const puedeGuardar = Boolean(archivo && mes && anio) && !guardando;

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title="Nuevo cuadre"
      description="Sube el archivo de Excel del período"
      size="md"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!puedeGuardar || existente.data?.existe === true}
            onClick={() => onSubir({ mes, anio, observaciones, archivo })}
          >
            Subir cuadre
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="No se pudo subir el cuadre">
            {error}
          </Alert>
        ) : null}

        {existente.data?.existe ? (
          <Alert variant="warning" title="Ese período ya tiene cuadre">
            {existente.data.mensaje}
          </Alert>
        ) : existente.data ? (
          <p className="flex items-center gap-2 text-xs text-emerald-700">
            <ShieldCheck className="size-3.5" aria-hidden="true" />
            {existente.data.mensaje}
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="cuadre-mes">
              Mes *
            </label>
            <Select
              id="cuadre-mes"
              value={mes}
              onChange={(evento) => setMes(evento.target.value)}
              className={CONTROL}
              aria-label="Mes"
            >
              {meses.map((item) => (
                <option key={item.valor} value={String(item.valor)}>
                  {item.nombre}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-700" htmlFor="cuadre-anio">
              Año *
            </label>
            <Select
              id="cuadre-anio"
              value={anio}
              onChange={(evento) => setAnio(evento.target.value)}
              className={CONTROL}
              aria-label="Año"
            >
              {anios.map((item) => (
                <option key={item} value={String(item)}>
                  {item}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink-700" htmlFor="cuadre-observaciones">
            Observaciones (opcional)
          </label>
          <textarea
            id="cuadre-observaciones"
            rows={3}
            maxLength={500}
            className={cn(CONTROL, 'h-auto py-2')}
            value={observaciones}
            placeholder="Notas del cuadre, banco, cuenta…"
            onChange={(evento) => setObservaciones(evento.target.value)}
          />
        </div>

        <ArchivoInput
          label="Archivo del cuadre *"
          accept={ACEPTADOS}
          nombre={archivo?.name ?? null}
          placeholder="Ningún archivo seleccionado"
          hint="Excel (.xlsx o .xls) de hasta 2 MB. Se guarda en el almacén de la API y se descarga con su nombre original."
          onSeleccionar={(elegido) => setArchivo(elegido?.archivo ?? null)}
        />
      </div>
    </Dialog>
  );
}

interface DialogoDetalleProps {
  cuadreId: number | null;
  consulta: DetalleCuadre | undefined;
  cargando: boolean;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onAnular: (cuadre: Cuadre) => void;
}

/** Ficha del cuadre, con su archivo y su integridad. */
export function DialogoDetalleCuadre({
  cuadreId,
  consulta,
  cargando,
  guardando,
  error,
  onCerrar,
  onAnular,
}: DialogoDetalleProps) {
  const cuadre = consulta?.cuadre;
  const integridad = consulta?.integridad;
  const [errorDescarga, setErrorDescarga] = useState<string | null>(null);

  const descargar = async () => {
    if (!cuadreId) return;

    setErrorDescarga(null);

    try {
      await abrirPdfAutenticado(cuadresApi.urlArchivo(cuadreId), {
        nombre: cuadre?.archivo ?? `cuadre-${cuadreId}.xlsx`,
      });
    } catch (fallo) {
      setErrorDescarga(fallo instanceof Error ? fallo.message : 'No se pudo descargar el archivo.');
    }
  };

  return (
    <Dialog
      open={Boolean(cuadreId)}
      onClose={onCerrar}
      title={cuadre ? `Cuadre de ${cuadre.periodo}` : 'Cuadre'}
      description={cuadre ? cuadre.archivo : undefined}
      size="md"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {cuadre?.activo ? (
              <Button
                variant="danger"
                size="sm"
                disabled={guardando}
                onClick={() => onAnular(cuadre)}
              >
                Anular cuadre
              </Button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {cuadre?.disponible ? (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Download className="size-3.5" aria-hidden="true" />}
                onClick={() => void descargar()}
              >
                Descargar
              </Button>
            ) : null}

            <Button variant="outline" size="sm" onClick={onCerrar}>
              Cerrar
            </Button>
          </div>
        </div>
      }
    >
      {cargando ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {error ? (
            <Alert variant="error" title="No se pudo completar la acción">
              {error}
            </Alert>
          ) : null}

          {errorDescarga ? (
            <Alert variant="error" title="No se pudo descargar el archivo">
              {errorDescarga}
            </Alert>
          ) : null}

          {cuadre && !cuadre.activo ? (
            <Alert variant="warning" title="Cuadre anulado">
              Lo anuló {cuadre.anulado_por ?? '—'}
              {cuadre.anulado_el ? ` el ${cuadre.anulado_el}` : ''}. Motivo: {cuadre.motivo_anulacion ?? '—'}
            </Alert>
          ) : null}

          {cuadre ? (
            <>
              <section className="rounded-card border border-ink-100 p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Dato etiqueta="Período" valor={cuadre.periodo} />
                  <Dato
                    etiqueta="Estado"
                    valor={
                      cuadre.activo ? (
                        <Badge variant="success">Activo</Badge>
                      ) : (
                        <Badge variant="danger">Anulado</Badge>
                      )
                    }
                  />
                  <div className="sm:col-span-2">
                    <Dato
                      etiqueta="Archivo"
                      valor={
                        <span className="flex items-center gap-2">
                          <FileSpreadsheet className="size-4 shrink-0 text-emerald-600" aria-hidden="true" />
                          <span className="break-all">{cuadre.archivo}</span>
                        </span>
                      }
                    />
                  </div>
                  <Dato etiqueta="Tamaño" valor={cuadre.tamanio_texto} />
                  <Dato etiqueta="Tipo" valor={cuadre.tipo} />
                  <Dato etiqueta="Subido" valor={formatDateValue(cuadre.creado?.slice(0, 10))} />
                  <Dato etiqueta="Subió" valor={cuadre.creado_por} />
                </div>
              </section>

              {cuadre.observaciones ? (
                <section className="rounded-card border border-ink-100 p-4">
                  <h3 className="text-sm font-semibold text-ink-800">Observaciones</h3>
                  <p className="mt-1 text-sm text-ink-600">{cuadre.observaciones}</p>
                </section>
              ) : null}

              <section className="rounded-card border border-ink-100 p-4">
                <h3 className="text-sm font-semibold text-ink-800">Integridad del archivo</h3>

                {integridad?.valido ? (
                  <p className="mt-1 flex items-start gap-2 text-sm text-emerald-700">
                    <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    {integridad.mensaje}
                  </p>
                ) : (
                  <p className="mt-1 flex items-start gap-2 text-sm text-rose-700">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    {integridad?.error ?? 'No se pudo comprobar el archivo.'}
                  </p>
                )}
              </section>
            </>
          ) : (
            <p className="text-sm text-ink-500">No se ha podido leer el cuadre.</p>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** Formulario de anulación: pide el motivo, como el original. */
export function DialogoAnularCuadre({
  cuadre,
  guardando,
  error,
  onCerrar,
  onConfirmar,
}: {
  cuadre: Cuadre | null;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState('');

  return (
    <Dialog
      open={Boolean(cuadre)}
      onClose={onCerrar}
      title="Anular el cuadre"
      description={cuadre ? `${cuadre.periodo} · ${cuadre.archivo}` : undefined}
      size="sm"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={guardando}
            disabled={!motivo.trim()}
            onClick={() => onConfirmar(motivo.trim())}
          >
            Anular cuadre
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <Alert variant="error" title="No se pudo anular">
            {error}
          </Alert>
        ) : null}

        <Input
          label="Motivo de la anulación *"
          value={motivo}
          maxLength={500}
          placeholder="Por qué se anula este cuadre"
          onChange={(evento) => setMotivo(evento.target.value)}
        />

        <p className="text-xs text-ink-500">
          El período queda libre para subir otro cuadre; el archivo y el registro se conservan con
          quién y cuándo se anuló.
        </p>
      </div>
    </Dialog>
  );
}
